/**
 * The settings, shown as the operator last left them rather than as the robot last said.
 *
 * A slider that snaps back while the robot's echo is in flight is unusable, so a change shows
 * at once and is only handed back to the robot's own value when that value agrees -- or after
 * a couple of seconds, which is how a refused change becomes visible instead of silently
 * sticking on screen.
 */
import { useRobotState } from "adamo-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { Sender } from "./actions";
import { differences, loadPreferences, rememberPreference } from "../state/preferences";
import type { Settings, Status } from "./types";

// How long a change is shown before the robot's own value is believed again. Long enough to
// cover a slow acknowledgement; short enough that a value the arm never took does not sit
// there looking accepted. When it expires without the robot agreeing, that is reported --
// silently snapping back is how an arm that ignores its settings looks like a flaky slider.
const FORGET_AFTER_MS = 2500;

const DEFAULTS: Settings = {
  gripper_open: 1, speed: 0.05, grip_close_speed: 0, mode: "crane", hand: "right", overlay: true,
};

type Pending = Partial<Record<keyof Settings, { value: unknown; at: number }>>;

export function useSettings(robot: string, status: Status | null, sender: Sender,
                            onIgnored?: (key: keyof Settings) => void) {
  const retained = useRobotState<Settings>(robot, "settings");
  const fromRobot: Settings = retained ?? status?.settings ?? DEFAULTS;
  // Whether this arm speaks the settings protocol at all. An older one publishes neither, and
  // then nothing sent to it can ever be confirmed.
  const understood = retained !== null || Boolean(status?.settings);
  const [pending, setPending] = useState<Pending>({});
  const ignored = useRef(onIgnored);
  ignored.current = onIgnored;
  const timer = useRef(0);

  // Drop an optimistic value once the robot agrees with it, or once it has had long enough
  // to have refused it.
  useEffect(() => {
    if (Object.keys(pending).length === 0) return;
    const tick = () => {
      setPending((current) => {
        const next: Pending = {};
        let changed = false;
        for (const [key, held] of Object.entries(current) as [keyof Settings, { value: unknown; at: number }][]) {
          const agreed = typeof held.value === "number"
            ? Math.abs((fromRobot[key] as number) - (held.value as number)) < 1e-3
            : fromRobot[key] === held.value;
          if (agreed) { changed = true; continue; }
          if (Date.now() - held.at > FORGET_AFTER_MS) {
            changed = true;
            ignored.current?.(key);
            continue;
          }
          next[key] = held;
        }
        return changed ? next : current;
      });
    };
    timer.current = window.setInterval(tick, 200);
    return () => window.clearInterval(timer.current);
  }, [pending, fromRobot]);

  const shown: Settings = { ...fromRobot };
  for (const [key, held] of Object.entries(pending) as [keyof Settings, { value: unknown; at: number }][]) {
    (shown as Record<string, unknown>)[key] = held.value;
  }

  const change = useCallback(<K extends keyof Settings>(key: K, value: Settings[K], final = true) => {
    setPending((current) => ({ ...current, [key]: { value, at: Date.now() } }));
    const text = typeof value === "number" ? String(Number(value.toFixed(3)))
               : typeof value === "boolean" ? (value ? "on" : "off")
               : String(value);
    // While a slider is moving, the robot only needs the newest number; the one that has to
    // arrive is the one the finger came off on.
    if (final) {
      sender.send(`set:${key}:${text}`);
      // this operator's taste, kept in this browser -- but not the task, which is what is in the
      // box today, not how somebody likes to drive
      if (key !== "task") rememberPreference(key, value);
    } else {
      sender.nudge(`set:${key}:${text}`);
    }
  }, [sender]);

  /**
   * What this browser remembers that the arm is not already set to.
   *
   * Offered, never applied on arrival. Putting them on automatically changed which hand drove
   * the arm the moment someone took the controls, so every button they then pressed did
   * nothing -- the robot was fine and the operator had no way to know why.
   */
  const savedDiffer = useMemo(() => differences(loadPreferences(), fromRobot), [fromRobot]);

  const applyPreferences = useCallback(() => {
    for (const [key, value] of savedDiffer) change(key as keyof Settings, value as never, true);
    return savedDiffer.length;
  }, [change, savedDiffer]);

  return { settings: shown, change, fromRobot, understood, applyPreferences, savedDiffer };
}
